import React, { useEffect, useMemo, useRef, useState } from 'react';
import { computeNiceTicks } from './chartUtils';

export interface LineChartPoint {
  label: string;
  value: number;
}

interface AnalyticsLineChartProps {
  title: string;
  data: LineChartPoint[];
  controls?: React.ReactNode;
  tooltipValueLabel?: string;
  percentScale?: boolean;
}

interface TooltipState {
  point: LineChartPoint;
  x: number;
  y: number;
}

const CHART_HEIGHT = 220;
const PADDING = { top: 20, right: 20, bottom: 8, left: 8 };
const PERCENT_TICKS = [100, 75, 50, 25, 0];

function formatTooltipValue(value: number, percentScale: boolean): string {
  if (!Number.isFinite(value)) {
    return percentScale ? '0%' : '0';
  }
  return percentScale ? `${Math.round(value)}%` : String(Math.round(value));
}

export default function AnalyticsLineChart({
  title,
  data,
  controls,
  tooltipValueLabel = 'Value',
  percentScale = false,
}: Readonly<AnalyticsLineChartProps>) {
  const plotRef = useRef<HTMLDivElement>(null);
  const [plotWidth, setPlotWidth] = useState(0);
  const [tooltip, setTooltip] = useState<TooltipState | null>(null);

  useEffect(() => {
    const element = plotRef.current;
    if (!element) {
      return undefined;
    }

    const updateWidth = () => {
      setPlotWidth(element.clientWidth);
    };

    updateWidth();
    const observer = new ResizeObserver(updateWidth);
    observer.observe(element);
    return () => observer.disconnect();
  }, [data.length]);

  const maxValue = Math.max(...data.map(d => d.value), 0);
  const yMax = percentScale ? 100 : Math.max(maxValue, 1);
  const yTicks = useMemo(() => {
    if (percentScale) {
      return PERCENT_TICKS;
    }
    const ticks = computeNiceTicks(yMax, 4);
    return ticks.length > 1 ? ticks : [yMax, 0];
  }, [percentScale, yMax]);
  const tickMax = yTicks[0] || yMax;

  const plotInnerHeight = CHART_HEIGHT - PADDING.top - PADDING.bottom;
  const plotInnerWidth = Math.max(plotWidth - PADDING.left - PADDING.right, 1);

  const points = useMemo(() => {
    if (!data.length || plotWidth <= 0) {
      return [];
    }
    const step = data.length > 1 ? plotInnerWidth / (data.length - 1) : 0;
    return data.map((point, index) => {
      const x = PADDING.left + step * index;
      const y = PADDING.top + (1 - point.value / tickMax) * plotInnerHeight;
      return { ...point, x, y };
    });
  }, [data, plotInnerWidth, plotWidth, tickMax, plotInnerHeight]);

  const linePath = useMemo(() => {
    if (points.length === 0) {
      return '';
    }
    return points
      .map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`)
      .join(' ');
  }, [points]);

  const areaPath = useMemo(() => {
    if (points.length === 0) {
      return '';
    }
    const baseline = PADDING.top + plotInnerHeight;
    const topLine = points
      .map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`)
      .join(' ');
    return `${topLine} L ${points[points.length - 1].x} ${baseline} L ${points[0].x} ${baseline} Z`;
  }, [points, plotInnerHeight]);

  const showTooltip = (point: (typeof points)[number], event: React.MouseEvent<SVGElement>) => {
    const plot = plotRef.current;
    if (!plot) {
      return;
    }
    const plotRect = plot.getBoundingClientRect();
    const targetRect = event.currentTarget.getBoundingClientRect();
    setTooltip({
      point,
      x: targetRect.left - plotRect.left + targetRect.width / 2,
      y: targetRect.top - plotRect.top,
    });
  };

  return (
    <section className="analytics-chart-card">
      <div className="analytics-chart-card-header">
        <h3 className="analytics-chart-card-title">{title}</h3>
        {controls ? <div className="analytics-chart-controls">{controls}</div> : null}
      </div>
      {data.length === 0 ? (
        <p className="analytics-empty">No data available</p>
      ) : (
        <div className="analytics-chart-body">
          <div
            className="analytics-y-axis"
            aria-hidden
          >
            {yTicks.map(tick => (
              <span
                key={`tick-${tick}`}
                className="analytics-y-tick"
              >
                {tick}
              </span>
            ))}
          </div>
          <div
            className="analytics-plot analytics-plot--line"
            ref={plotRef}
          >
            {tooltip ? (
              <div
                className="analytics-chart-tooltip-float analytics-chart-tooltip-float--line"
                style={{ left: `${tooltip.x}px`, top: `${tooltip.y}px` }}
                role="tooltip"
              >
                <strong>{tooltip.point.label}</strong>
                <span className="analytics-tooltip-metric">
                  <span className="analytics-legend-swatch analytics-legend-swatch--line" />
                  {tooltipValueLabel} {formatTooltipValue(tooltip.point.value, percentScale)}
                </span>
              </div>
            ) : null}
            {plotWidth > 0 ? (
              <svg
                className="analytics-line-svg"
                width={plotWidth}
                height={CHART_HEIGHT}
                viewBox={`0 0 ${plotWidth} ${CHART_HEIGHT}`}
                aria-hidden
              >
                {yTicks.map(tick => {
                  const y = PADDING.top + (1 - tick / tickMax) * plotInnerHeight;
                  return (
                    <line
                      key={`grid-${tick}`}
                      x1={PADDING.left}
                      x2={plotWidth - PADDING.right}
                      y1={y}
                      y2={y}
                      className="analytics-line-grid"
                    />
                  );
                })}
                {areaPath ? (
                  <path
                    d={areaPath}
                    className="analytics-line-area"
                  />
                ) : null}
                {linePath ? (
                  <path
                    d={linePath}
                    className="analytics-line-path"
                  />
                ) : null}
                {points.map(point => (
                  <circle
                    key={point.label}
                    cx={point.x}
                    cy={point.y}
                    r={4}
                    className="analytics-line-point"
                    onMouseEnter={event => showTooltip(point, event)}
                    onMouseLeave={() => setTooltip(null)}
                  />
                ))}
              </svg>
            ) : null}
            <div className="analytics-line-labels">
              {data.map(point => (
                <span
                  key={point.label}
                  className="analytics-bar-label"
                  title={point.label}
                >
                  {point.label}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
